import AddCircleIcon from "@mui/icons-material/AddCircle";
import {
  Box,
  Button,
  Container,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Skeleton from "@mui/material/Skeleton";
import { styled } from "@mui/material/styles";
import { useEffect, useMemo, useRef } from "react";

import { useTranslation } from "common/locales";
import NoFieldsModal from "features/fieldEdit/NoFieldsModal";
import type { FeatureCollection } from "geojson";
import {
  getAllArableFieldGeomsFromFields,
  getAllFieldGeomsFromFields,
  useArableFields,
  useCurrentFarm,
  useFields,
  useUserFarms,
} from "model";
import { useNavigate } from "react-router-dom";
import { useAppStore, useUIStore } from "stores";
import { AnalyticsEvents, analytics } from "../../common/analytics";
import AnimalGroupCards from "./AnimalGroupCards";
import FarmMetricsCards from "./FarmMetricsCards";
import FieldTypeCards from "./FieldTypeCards";

export default function FarmOverviewContent() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const user = useAppStore((state) => state.user);
  const isCorporateUser = !!(
    user?.corporations?.length && user.corporations.length > 0
  );
  const { t } = useTranslation();

  // Use React Query hooks and Zustand
  const { data: farm } = useCurrentFarm();
  // Use direct Zustand access for loading state
  const loading = useAppStore((state) => state.loading) === "pending";
  const { isLoading: farmsLoading } = useUserFarms();

  const setZoomToGeom = useUIStore((state) => state.setZoomToGeom);
  const setIsEditing = useUIStore((state) => state.setIsEditing);
  const sbiImportModalOpen = useUIStore((state) => state.sbiImportModalOpen);
  const openSbiImportModal = useUIStore((state) => state.openSbiImportModal);
  const closeSbiImportModal = useUIStore((state) => state.closeSbiImportModal);
  const navigate = useNavigate();

  // Use React Query for fields data
  const { data: fields = [], isLoading: fieldsLoading } = useFields();
  const { data: arableFields = [], isLoading: arableFieldsLoading } =
    useArableFields();

  // Compute geoms from React Query data
  const fieldGeoms = useMemo(
    () => getAllFieldGeomsFromFields(fields),
    [fields]
  );
  const arableFieldGeoms = useMemo(
    () => getAllArableFieldGeomsFromFields(arableFields),
    [arableFields]
  );
  const allFieldGeoms = useMemo<FeatureCollection>(
    () => ({
      type: "FeatureCollection",
      features: [...fieldGeoms.features, ...arableFieldGeoms.features],
    }),
    [fieldGeoms, arableFieldGeoms]
  );

  // Count total fields (grass + arable)
  const totalFieldCount =
    fieldGeoms.features.length + arableFieldGeoms.features.length;

  // Track which farm we've zoomed to, so we only zoom once per farm
  const zoomedToFarmRef = useRef<string | null>(null);

  useEffect(() => {
    // Only zoom if we haven't already zoomed to this farm
    if (zoomedToFarmRef.current === farm?.guid) {
      return;
    }

    if (fieldsLoading || arableFieldsLoading) {
      return;
    }

    // Zoom to the full loaded field extent, including arable fields.
    if (allFieldGeoms.features.length > 0) {
      zoomedToFarmRef.current = farm?.guid ?? null;
      setZoomToGeom(allFieldGeoms);
    } else if (farm?.geom) {
      // Only use farm geom as fallback when fields have finished loading and there are none
      zoomedToFarmRef.current = farm?.guid ?? null;
      setZoomToGeom(farm.geom as FeatureCollection);
    }
  }, [
    farm?.guid,
    farm?.geom,
    allFieldGeoms,
    fieldsLoading,
    arableFieldsLoading,
    setZoomToGeom,
  ]);

  const handleAddMoreFields = () => {
    analytics.track(AnalyticsEvents.ADD_MORE_FIELDS_CLICKED);
    if (totalFieldCount < 5) {
      // Show SBI import dialog for farms with less than 5 fields
      openSbiImportModal();
    } else {
      // Go directly to manual field drawing for farms with 5+ fields
      setIsEditing(true);
      navigate(`/add-field/enter-details`);
    }
  };

  return (
    <FarmOverviewWrapper>
      <NoFieldsModal open={sbiImportModalOpen} onClose={closeSbiImportModal} />
      <Container data-scrollable="false">
        <div />
      </Container>
      {loading && (
        <FarmTotals data-scrollable="false">
          <Box sx={{ px: 3, pb: 2 }}>
            {/* Metrics Cards Skeleton */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 1,
              }}
            >
              <Skeleton
                variant="rectangular"
                height={100}
                sx={{ borderRadius: 2 }}
              />
              <Skeleton
                variant="rectangular"
                height={100}
                sx={{ borderRadius: 2 }}
              />
            </Box>
          </Box>
        </FarmTotals>
      )}
      {loading && (
        <Box sx={{ backgroundColor: "white", px: 3, pb: 2 }}>
          {/* Livestock Section Skeleton */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Skeleton variant="text" width={80} height={24} />
            <Skeleton variant="text" width={120} height={32} />
          </Box>
          <Box sx={{ display: "flex", gap: 2, overflow: "hidden" }}>
            {[1, 2].map((i) => (
              <Skeleton
                key={i}
                variant="rectangular"
                sx={{ minWidth: 280, height: 220, borderRadius: 4 }}
              />
            ))}
          </Box>
        </Box>
      )}
      {loading && (
        <Box sx={{ backgroundColor: "white", px: 3, pt: 2, pb: 2 }}>
          {/* Fields Section Skeleton */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Skeleton variant="text" width={50} height={24} />
            <Skeleton variant="text" width={80} height={32} />
          </Box>
          <Box sx={{ display: "flex", gap: 2, overflow: "hidden" }}>
            {[1, 2].map((i) => (
              <Skeleton
                key={i}
                variant="rectangular"
                sx={{ minWidth: 280, height: 200, borderRadius: 4 }}
              />
            ))}
          </Box>
        </Box>
      )}
      {!loading && (
        <FarmTotals data-scrollable="false">
          <Box sx={{ px: 3, pb: 2 }}>
            <FarmMetricsCards />
          </Box>
        </FarmTotals>
      )}
      <Box
        sx={{
          backgroundColor: "white",
          px: 3,
        }}
      >
        {!isCorporateUser && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                fontWeight: 600,
              }}
            >
              {t("farm.livestock.label")}
            </Typography>
            <Button
              startIcon={<AddCircleIcon sx={{ fontSize: "16px !important" }} />}
              size="small"
              onClick={() => {
                analytics.track(AnalyticsEvents.ANIMAL_GROUP_ADD_CLICKED);
                navigate("/add-animal-group");
              }}
              sx={{
                color: "primary.main",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.8rem",
                padding: "4px 8px",
                minWidth: "auto",
                "&:hover": {
                  backgroundColor: "rgba(56, 142, 60, 0.08)",
                },
              }}
            >
              {t("farm.livestock.addAnimalGroup")}
            </Button>
          </Box>
        )}
        <AnimalGroupCards />
      </Box>
      <Box
        sx={{
          backgroundColor: "white",
          px: 3,
          pt: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              fontWeight: 600,
            }}
          >
            {t("farm.fields.label")}
          </Typography>
          {!isCorporateUser && (
            <Button
              startIcon={<AddCircleIcon sx={{ fontSize: "16px !important" }} />}
              size="small"
              onClick={handleAddMoreFields}
              sx={{
                color: "primary.main",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.8rem",
                padding: "4px 8px",
                minWidth: "auto",
                "&:hover": {
                  backgroundColor: "rgba(56, 142, 60, 0.08)",
                },
              }}
            >
              {t("farm.fields.addField")}
            </Button>
          )}
        </Box>
        <FieldTypeCards />
      </Box>
    </FarmOverviewWrapper>
  );
}

const FarmOverviewWrapper = styled(Box)({
  display: "flex",
  flexDirection: "column",
  flex: 1,
  minHeight: "100%",
  backgroundColor: "white",
  overflowX: "hidden",
});

const FarmTotals = styled(Box)({
  backgroundColor: "white",
});
